import React, { useState } from 'react';
import { Download, Sparkles, Menu, X, ArrowRight, ShieldCheck, BookOpen, Smartphone, Monitor } from 'lucide-react';
import { CkLogo } from './CkLogo';

interface NavbarProps {
  onOpenDownloads: () => void;
  onOpenGuide: (tab?: 'android' | 'windows' | 'security') => void;
  onLaunchWebApp: () => void;
  hasActiveRoom?: boolean;
  onViewLiveConsole?: () => void;
  onNavigateHome?: () => void;
  onOpenFlow?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  onOpenDownloads,
  onOpenGuide,
  onLaunchWebApp,
  hasActiveRoom,
  onViewLiveConsole,
  onNavigateHome,
  onOpenFlow
}) => {

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: 'Features', href: '#features', type: 'scroll' as const },
    { label: 'How It Works', href: '#how-it-works', type: 'scroll' as const },
    { label: 'Downloads', href: '#downloads', type: 'scroll' as const },
    { label: 'Guide', href: '#guide', type: 'guide' as const },
    { label: 'Security', href: '#security', type: 'security' as const },
    { label: 'FAQ', href: '#faq', type: 'scroll' as const },
  ];

  const handleNavClick = (
    e: React.MouseEvent,
    link: { label: string; href: string; type: 'scroll' | 'guide' | 'security' }
  ) => {
    e.preventDefault();
    setMobileMenuOpen(false);

    if (link.type === 'guide') {
      onOpenGuide('android');
      return;
    }

    if (link.type === 'security') {
      onOpenGuide('security');
      return;
    }

    // If we're on console view, switch to landing view first
    if (onNavigateHome) {
      onNavigateHome();
    }

    if (link.href && link.href.startsWith('#')) {
      const targetId = link.href.substring(1);
      setTimeout(() => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 60);
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (onNavigateHome) {
      onNavigateHome();
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className="sticky top-0 z-40 w-full backdrop-blur-xl bg-[#080a12]/85 border-b border-white/10 transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-18 flex items-center justify-between">
        
        {/* Brand Logo */}
        <button
          onClick={handleLogoClick}
          className="flex items-center gap-3 group text-left focus:outline-none"
          title="CK Share Home"
        >
          <CkLogo size={38} className="group-hover:scale-105 transition-transform drop-shadow-[0_0_15px_rgba(0,240,255,0.4)]" />
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-lg text-white font-display tracking-tight">
                CK <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">Share</span>
              </span>
              <span className="px-2 py-0.2 text-[10px] font-bold text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded-full">
                v1.3.0
              </span>
            </div>
            <span className="text-[10px] text-gray-400 hidden sm:inline">Universal E2EE Clipboard Mesh</span>
          </div>
        </button>

        {/* Desktop Nav Links */}
        <nav className="hidden md:flex items-center gap-6 text-xs font-medium text-gray-300">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={(e) => handleNavClick(e, link)}
              className="hover:text-cyan-300 transition-colors focus:outline-none cursor-pointer py-1 px-1.5 rounded-lg hover:bg-white/5"
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Desktop Action Buttons */}
        <div className="hidden md:flex items-center gap-3">
          {onOpenFlow && (
            <button
              onClick={onOpenFlow}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-cyan-300 hover:text-white bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-xl transition-all cursor-pointer shadow-[0_0_12px_rgba(0,240,255,0.15)]"
              title="Open CK Flow Multi-PC Mouse Sharing"
            >
              <span>🖱️ CK Flow</span>
            </button>
          )}

          <button
            onClick={() => onOpenGuide('android')}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all"
            title="Open Setup Guide"
          >
            <BookOpen size={14} className="text-emerald-400" />
            <span>Guide</span>
          </button>

          <button
            onClick={() => onOpenGuide('security')}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all"
            title="Open E2EE Security Whitepaper"
          >
            <ShieldCheck size={14} className="text-cyan-400" />
            <span>Security</span>
          </button>

          <button
            onClick={onOpenDownloads}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-200 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all"
          >
            <Download size={14} className="text-cyan-400" />
            <span>Download Apps</span>
          </button>

          {hasActiveRoom && onViewLiveConsole ? (
            <button
              onClick={onViewLiveConsole}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-black bg-gradient-to-r from-cyan-400 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.35)] transition-all animate-pulse"
            >
              <Sparkles size={14} />
              <span>Live Console Active</span>
            </button>
          ) : (
            <button
              onClick={onLaunchWebApp}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-black bg-cyan-400 hover:bg-cyan-300 rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.35)] hover:shadow-[0_0_25px_rgba(0,240,255,0.5)] transition-all"
            >
              <Sparkles size={14} />
              <span>Launch Web App</span>
            </button>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <div className="flex md:hidden items-center gap-2">
          <button
            onClick={hasActiveRoom && onViewLiveConsole ? onViewLiveConsole : onLaunchWebApp}
            className="px-3 py-1.5 text-xs font-bold text-black bg-cyan-400 rounded-lg shadow-sm"
          >
            {hasActiveRoom ? 'Live Console' : 'Launch'}
          </button>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5"
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

      </div>

      {/* Mobile Dropdown Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden px-4 pt-2 pb-6 bg-[#0c0e18] border-b border-white/10 space-y-3 animate-fadeIn">
          <div className="flex flex-col space-y-1 text-sm font-medium text-gray-300">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={(e) => handleNavClick(e, link)}
                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-white/5 hover:text-cyan-300 transition-colors flex items-center justify-between"
              >
                <span>{link.label}</span>
                {link.type === 'guide' && <BookOpen size={14} className="text-emerald-400" />}
                {link.type === 'security' && <ShieldCheck size={14} className="text-cyan-400" />}
              </button>
            ))}
          </div>

          <div className="pt-3 border-t border-white/10 flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onOpenGuide('android');
                }}
                className="flex items-center justify-center gap-1.5 py-2.5 px-3 text-xs font-semibold text-gray-200 bg-white/5 border border-white/10 rounded-xl"
              >
                <BookOpen size={14} className="text-emerald-400" />
                <span>Setup Guide</span>
              </button>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onOpenGuide('security');
                }}
                className="flex items-center justify-center gap-1.5 py-2.5 px-3 text-xs font-semibold text-gray-200 bg-white/5 border border-white/10 rounded-xl"
              >
                <ShieldCheck size={14} className="text-cyan-400" />
                <span>Security Doc</span>
              </button>
            </div>

            <button
              onClick={() => {
                setMobileMenuOpen(false);
                onOpenDownloads();
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-xs font-semibold text-gray-200 bg-white/5 border border-white/10 rounded-xl"
            >
              <Download size={14} className="text-cyan-400" />
              <span>Download Android APK &amp; Windows</span>
            </button>

            <button
              onClick={() => {
                setMobileMenuOpen(false);
                if (hasActiveRoom && onViewLiveConsole) {
                  onViewLiveConsole();
                } else {
                  onLaunchWebApp();
                }
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-xs font-bold text-black bg-cyan-400 rounded-xl"
            >
              <Sparkles size={14} />
              <span>{hasActiveRoom ? 'Open Live Sync Console' : 'Launch Web App & Connect'}</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
